import { Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Confetti } from "@/components/illustrations/Confetti";
import { KLSkyline } from "@/components/illustrations/KLSkyline";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EnquiryForm } from "@/components/contact/EnquiryForm";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { absoluteUrl, pageTitle } from "@/seo/site";
import { organizationLd, webPageLd } from "@/seo/structuredData";
import { BrandHeadingSuffix } from "@/components/seo/BrandHeadingSuffix";

const CONTACT_EMAIL = "BBCKLRR@bizedge.com";

const CONTACT_DESCRIPTION =
  "Contact the Bursa Bull Charge (BBC) × The Edge KL Rat Race 2026 team for registration, sponsorship, and general enquiries. Email BBCKLRR@bizedge.com.";

export default function ContactPage() {
  const { t } = useTranslation("contact");
  useDocumentMeta({
    title: pageTitle("Contact"),
    description: CONTACT_DESCRIPTION,
    canonicalUrl: absoluteUrl("/contact/"),
    jsonLd: [
      organizationLd(),
      webPageLd({
        name: "Contact",
        description: CONTACT_DESCRIPTION,
        path: "/contact/",
        breadcrumb: [
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact/" },
        ],
      }),
    ],
  });

  return (
    <>
      <PageHero />

      <section className="container py-16 md:py-20">
        <Card>
          <CardHeader className="pb-4">
            <p className="text-xs uppercase tracking-[0.25em] text-secondary font-semibold">{t("form.eyebrow")}</p>
            <CardTitle className="mt-1 text-3xl">{t("form.title")}</CardTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("form.description")}
            </p>
          </CardHeader>
          <CardContent>
            <EnquiryForm />
          </CardContent>
        </Card>

        {/* Shared email — mirrors the footer contact row. */}
        <div className="mt-8 flex items-start gap-2 text-sm text-muted-foreground">
          <Mail className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <span>
            {t("email.prefer")}{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-foreground transition-colors hover:text-primary break-all cursor-pointer"
            >
              {CONTACT_EMAIL}
            </a>
          </span>
        </div>
      </section>
    </>
  );
}

function PageHero() {
  const { t } = useTranslation("contact");
  return (
    <section className="relative overflow-hidden bg-hero-sky text-surface-deep-foreground">
      {/*
       * Poster-faithful overlay (light): faint confetti texture + KL skyline
       * silhouette, matching the Race Day / Sponsorship heroes so Contact
       * reads as part of the same site.
       */}
      <Confetti className="absolute inset-0 opacity-50" density="low" />
      <KLSkyline
        className="absolute bottom-0 right-0 hidden h-40 w-1/2 md:block lg:h-48"
        toneClassName="text-surface-deep-foreground/12"
      />

      <div className="container relative py-16 md:py-24">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">{t("hero.eyebrow")}</p>
          <h1 className="mt-3 text-4xl md:text-6xl">
            {t("hero.heading")}
            <BrandHeadingSuffix />
          </h1>
          <p className="mt-4 text-surface-deep-foreground/80">
            {t("hero.descriptionLine1")} <br /> {t("hero.descriptionLine2")}
          </p>
        </div>
      </div>
    </section>
  );
}
